/**
 * Analytics Settings Service
 *
 * Manages visibility preferences for analytics page sections.
 * Settings are persisted to localStorage.
 */

// ============================================================
// Types
// ============================================================

export interface AnalyticsSectionVisibility {
  cashFlowScore: boolean;
  spendingByCategory: boolean;
  topIncome: boolean;  // Top income breakdown
  watchlist: boolean;  // Smart keyword tracker (crypto/AI/custom groups)
  monthOverMonth: boolean;
  payCycles: boolean;
  upcomingCashFlow: boolean;
  recurringBills: boolean;
  insights: boolean;
  monthlyTrends: boolean;
  dailyBalance: boolean;
}

export interface AnalyticsSettings {
  visibility: AnalyticsSectionVisibility;
}

// ============================================================
// Constants
// ============================================================

const STORAGE_KEY = 'ninjacash-analytics-settings';

const DEFAULT_VISIBILITY: AnalyticsSectionVisibility = {
  cashFlowScore: true,
  spendingByCategory: true,
  topIncome: true,  // Show income breakdown by default
  watchlist: true,  // Keyword tracker on by default (toggleable here + in panel)
  monthOverMonth: true,
  payCycles: true,
  upcomingCashFlow: true,
  recurringBills: true,
  insights: true,
  monthlyTrends: true,
  dailyBalance: true,
};

const DEFAULT_SETTINGS: AnalyticsSettings = {
  visibility: DEFAULT_VISIBILITY,
};

// ============================================================
// Section Metadata
// ============================================================

export interface SectionMeta {
  id: keyof AnalyticsSectionVisibility;
  name: string;
  description: string;
  icon: string;
}

export const ANALYTICS_SECTIONS: SectionMeta[] = [
  {
    id: 'cashFlowScore',
    name: 'Cash Flow Score',
    description: 'Financial health indicator (0-100)',
    icon: '💯',
  },
  {
    id: 'spendingByCategory',
    name: 'Spending by Category',
    description: 'Donut chart showing expense breakdown',
    icon: '🍩',
  },
  {
    id: 'topIncome',
    name: 'Top Income',
    description: 'Income breakdown with amounts',
    icon: '💵',
  },
  {
    id: 'watchlist',
    name: 'Watchlist',
    description: 'Track keywords like btc, solana, claude, chatgpt',
    icon: '🔎',
  },
  {
    id: 'monthOverMonth',
    name: 'Month-over-Month',
    description: 'Compare current vs previous month',
    icon: '📊',
  },
  {
    id: 'payCycles',
    name: 'Pay Cycles',
    description: 'Income sources and schedules',
    icon: '💰',
  },
  {
    id: 'upcomingCashFlow',
    name: 'Upcoming Cash Flow',
    description: 'Projected balance over next 3 weeks',
    icon: '📈',
  },
  {
    id: 'recurringBills',
    name: 'Recurring Bills',
    description: 'Monthly bills by due date',
    icon: '📅',
  },
  {
    id: 'insights',
    name: 'Insights & Warnings',
    description: 'Smart recommendations and alerts',
    icon: '💡',
  },
  {
    id: 'monthlyTrends',
    name: 'Monthly Trends',
    description: 'Income vs Expenses over time',
    icon: '📉',
  },
  {
    // Historical id kept for stored-settings compatibility; the section
    // renders the monthly Net Trend chart (was mislabeled "Daily Balance").
    id: 'dailyBalance',
    name: 'Net Trend',
    description: 'Monthly net (income minus expenses) over time',
    icon: '〰️',
  },
];

// ============================================================
// Service Functions
// ============================================================

/**
 * Load analytics settings from localStorage.
 */
export function loadAnalyticsSettings(): AnalyticsSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle missing keys
      return {
        visibility: { ...DEFAULT_VISIBILITY, ...parsed.visibility },
      };
    }
  } catch (err) {
    console.error('[AnalyticsSettings] Error loading:', err);
  }
  return { ...DEFAULT_SETTINGS };
}

/**
 * Save analytics settings to localStorage.
 */
export function saveAnalyticsSettings(settings: AnalyticsSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (err) {
    console.error('[AnalyticsSettings] Error saving:', err);
  }
}

/**
 * Get visibility for a specific section.
 */
export function getSectionVisibility(sectionId: keyof AnalyticsSectionVisibility): boolean {
  const settings = loadAnalyticsSettings();
  return settings.visibility[sectionId] ?? true;
}

/**
 * Set visibility for a specific section.
 */
export function setSectionVisibility(
  sectionId: keyof AnalyticsSectionVisibility,
  visible: boolean
): void {
  const settings = loadAnalyticsSettings();
  settings.visibility[sectionId] = visible;
  saveAnalyticsSettings(settings);
}

/**
 * Toggle visibility for a specific section.
 */
export function toggleSectionVisibility(sectionId: keyof AnalyticsSectionVisibility): boolean {
  const settings = loadAnalyticsSettings();
  const newValue = !settings.visibility[sectionId];
  settings.visibility[sectionId] = newValue;
  saveAnalyticsSettings(settings);
  return newValue;
}

/**
 * Reset all visibility settings to defaults.
 */
export function resetAnalyticsSettings(): void {
  saveAnalyticsSettings({ ...DEFAULT_SETTINGS });
}

/**
 * Get all section visibility settings.
 */
export function getAllVisibility(): AnalyticsSectionVisibility {
  return loadAnalyticsSettings().visibility;
}

/**
 * Set all visibility settings at once.
 */
export function setAllVisibility(visibility: Partial<AnalyticsSectionVisibility>): void {
  const settings = loadAnalyticsSettings();
  settings.visibility = { ...settings.visibility, ...visibility };
  saveAnalyticsSettings(settings);
}
